import { useEffect, useRef, useState } from "react";

interface Metric {
  value: number;
  label: string;
  suffix: string;
  decimal?: boolean;
}

const METRICS: Metric[] = [
  { value: 300000, label: "Transacciones / día", suffix: "+" },
  { value: 99.95, label: "SLA garantizado", suffix: "%", decimal: true },
  { value: 2, label: "Latencia promedio", suffix: "s" },
  { value: 100, label: "Trazabilidad DGII", suffix: "%" },
];

function useCountUp(target: number, decimal = false, active = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + increment, target);
      setCount(current);
      if (current >= target) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [target, active]);

  if (decimal) return count.toFixed(2);
  return Math.floor(count).toLocaleString("es-DO");
}

function MetricCard({ metric }: { metric: Metric }) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setActive(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const display = useCountUp(metric.value, metric.decimal, active);

  return (
    <div ref={ref} className="text-center group">
      <div className="text-4xl md:text-5xl font-black text-white mb-2 tabular-nums">
        {metric.suffix === "s" ? (
          <>
            <span className="text-emerald-400">&lt;</span>{display}
            <span className="text-emerald-400 ml-1 text-2xl">{metric.suffix}</span>
          </>
        ) : (
          <>
            {display}
            <span className="text-blue-400 ml-1 text-2xl md:text-3xl">{metric.suffix}</span>
          </>
        )}
      </div>
      <p className="text-sm text-slate-400 font-medium">{metric.label}</p>
    </div>
  );
}

export default function MetricsSection() {
  return (
    <section className="bg-slate-950 py-20 px-4 md:px-6 border-t border-b border-slate-800">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 md:gap-14">
          {METRICS.map((m) => (
            <MetricCard key={m.label} metric={m} />
          ))}
        </div>
      </div>
    </section>
  );
}